import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Copy, AlertTriangle } from 'lucide-react';
import type { AdminSettings } from '@kaba/shared';
import { api } from '../lib/api';
import { toast } from '../lib/toast';
import { FormField, inputCls } from './AdminPage';
import { PasswordInput } from './ui/PasswordInput';
import { Button, cx } from './ui/ui';

const PRESETS: { name: string; issuer: string; label: string; hint: string }[] = [
  {
    name: 'Microsoft 365 / Entra ID',
    issuer: 'https://login.microsoftonline.com/<Verzeichnis-ID>/v2.0',
    label: 'Mit Microsoft anmelden',
    hint: 'Entra ID → App-Registrierungen → Neue Registrierung (Web, Umleitungs-URI siehe unten). Verzeichnis-ID und Anwendungs-ID stehen in der Übersicht, das Secret unter „Zertifikate & Geheimnisse“.',
  },
  {
    name: 'Google Workspace',
    issuer: 'https://accounts.google.com',
    label: 'Mit Google anmelden',
    hint: 'Google Cloud Console → APIs & Dienste → Anmeldedaten → OAuth-Client-ID (Webanwendung). Mit „Erlaubte Domains“ auf eure Workspace-Domain beschränken.',
  },
  {
    name: 'Authentik',
    issuer: 'https://auth.example.de/application/o/<slug>/',
    label: 'Mit Vereins-Konto anmelden',
    hint: 'Authentik → Anwendungen → Anbieter „OAuth2/OpenID“ (vertraulich) anlegen, Umleitungs-URI siehe unten.',
  },
  {
    name: 'Keycloak',
    issuer: 'https://sso.example.de/realms/<realm>',
    label: 'Mit Single Sign-on anmelden',
    hint: 'Keycloak → Clients → Client anlegen (OpenID Connect, Client-Authentifizierung an), gültige Umleitungs-URI siehe unten.',
  },
];

export function OidcSettingsForm({ settings }: { settings: AdminSettings }) {
  const qc = useQueryClient();
  const o = settings.oidc;
  const [enabled, setEnabled] = useState(o.enabled);
  const [label, setLabel] = useState(o.label || 'Mit Single Sign-on anmelden');
  const [issuer, setIssuer] = useState(o.issuer);
  const [clientId, setClientId] = useState(o.clientId);
  const [secret, setSecret] = useState('');
  const [scopes, setScopes] = useState(o.scopes);
  const [autoCreate, setAutoCreate] = useState(o.autoCreate);
  const [domains, setDomains] = useState(o.allowedDomains);
  const [hidePassword, setHidePassword] = useState(o.hidePassword);
  const [hint, setHint] = useState<string | null>(null);
  const [check, setCheck] = useState<{ ok: boolean; message: string } | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          const res = await api.put<AdminSettings>('/api/admin/settings/oidc', {
            enabled,
            label: label.trim(),
            issuer: issuer.trim(),
            clientId: clientId.trim(),
            clientSecret: secret || undefined,
            scopes: scopes.trim(),
            autoCreate,
            allowedDomains: domains.trim(),
            hidePassword,
          });
          qc.setQueryData(['admin-settings'], res);
          void qc.invalidateQueries({ queryKey: ['config'] });
          setSecret('');
          toast.info(enabled ? 'Gespeichert – die Anmeldeseite zeigt jetzt den Knopf.' : 'Gespeichert.');
        } catch (err) {
          toast.error((err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <p className="text-sm text-[var(--ink-soft)]">
        Anmeldung über den Anmeldedienst eurer Organisation (OpenID Connect) – z. B. Microsoft 365, Google Workspace, Authentik oder Keycloak.
        Passwort und Passkeys funktionieren weiterhin.
      </p>

      <label className="flex min-h-11 items-center gap-3 text-sm font-medium">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="size-5 accent-[var(--color-accent)]" />
        Anmeldung über Single Sign-on einschalten
      </label>

      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p.name}
            type="button"
            className="min-h-9 rounded-full border border-[var(--line)] px-3 text-xs hover:border-accent"
            onClick={() => {
              if (!issuer || window.confirm(`Adresse und Beschriftung für „${p.name}“ vorbelegen?`)) {
                setIssuer(p.issuer);
                setLabel(p.label);
                setHint(p.hint);
                setCheck(null);
              }
            }}
          >
            {p.name}
          </button>
        ))}
      </div>
      {hint && <p className="rounded-lg bg-accent-soft/50 p-3 text-xs">{hint}</p>}

      <FormField label="Umleitungs-URI (beim Anbieter eintragen)" hint="Auch „Redirect URI“ oder „Callback URL“ genannt.">
        <div className="flex gap-2">
          <input className={cx(inputCls, 'font-mono text-xs sm:text-xs')} value={o.callbackUrl} readOnly onFocus={(e) => e.target.select()} aria-label="Umleitungs-URI" />
          <Button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(o.callbackUrl);
                toast.info('Kopiert.');
              } catch {
                toast.info('Bitte markieren und kopieren.');
              }
            }}
          >
            <Copy size={16} />
          </Button>
        </div>
      </FormField>

      <FormField label="Adresse des Anmeldedienstes (Issuer)" htmlFor="oidc-issuer" hint="Die Basisadresse oder direkt die …/.well-known/openid-configuration.">
        <div className="flex gap-2">
          <input id="oidc-issuer" className={inputCls} value={issuer} onChange={(e) => (setIssuer(e.target.value), setCheck(null))} placeholder="https://…" inputMode="url" />
          <Button
            type="button"
            disabled={!issuer.trim()}
            onClick={async () => {
              setCheck(null);
              setCheck(await api.post<{ ok: boolean; message: string }>('/api/admin/settings/oidc/test', { issuer: issuer.trim() }));
            }}
          >
            Prüfen
          </Button>
        </div>
      </FormField>
      {check && (
        <p role="status" className={cx('flex items-start gap-2 text-sm', check.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300')}>
          {check.ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
          {check.message}
        </p>
      )}
      <FormField label="Client-ID" htmlFor="oidc-client">
        <input id="oidc-client" className={inputCls} value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" />
      </FormField>
      <FormField
        label="Client-Secret"
        htmlFor="oidc-secret"
        hint={
          o.secretUnreadable
            ? 'Das gespeicherte Secret ist nicht mehr lesbar (KABA_SECRET geändert?) – bitte neu eintragen.'
            : o.hasSecret
              ? 'Gespeichert. Leer lassen, um es zu behalten.'
              : 'Wird verschlüsselt gespeichert und nie wieder angezeigt.'
        }
      >
        <PasswordInput id="oidc-secret" className={inputCls} value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="new-password" placeholder={o.hasSecret ? '••••••••' : ''} />
      </FormField>
      <FormField label="Beschriftung des Knopfs" htmlFor="oidc-label">
        <input id="oidc-label" className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} />
      </FormField>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium">Neue Personen</legend>
        <label className="flex items-start gap-3 rounded-xl border border-[var(--line)] p-3">
          <input type="checkbox" checked={autoCreate} onChange={(e) => setAutoCreate(e.target.checked)} className="mt-0.5 size-5 accent-[var(--color-accent)]" />
          <span>
            <span className="block text-sm font-medium">Konto beim ersten Anmelden automatisch anlegen</span>
            <span className="text-xs text-[var(--ink-soft)]">
              Aus: Nur wer schon ein Konto hat oder zu einem Board eingeladen wurde, kommt hinein. Bestehende Konten mit derselben E-Mail-Adresse
              werden immer verbunden.
            </span>
          </span>
        </label>
        <FormField label="Erlaubte E-Mail-Domains (optional)" htmlFor="oidc-domains" hint="Kommagetrennt, z. B. „firma.de, firma-bw.de“. Gilt für neue Konten; leer = alle, die der Anmeldedienst zulässt.">
          <input id="oidc-domains" className={inputCls} value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="firma.de" />
        </FormField>
      </fieldset>

      <label className="flex items-start gap-3 rounded-xl border border-[var(--line)] p-3">
        <input type="checkbox" checked={hidePassword} onChange={(e) => setHidePassword(e.target.checked)} className="mt-0.5 size-5 accent-[var(--color-accent)]" />
        <span>
          <span className="block text-sm font-medium">Passwort-Anmeldung einklappen</span>
          <span className="text-xs text-[var(--ink-soft)]">
            Die Anmeldeseite zeigt dann nur Single Sign-on und Passkey; „Mit Passwort anmelden“ bleibt als kleiner Link (Notzugang für Admins).
          </span>
        </span>
      </label>

      <details className="text-sm">
        <summary className="min-h-11 cursor-pointer py-2 text-[var(--ink-soft)]">Erweitert</summary>
        <FormField label="Scopes" htmlFor="oidc-scopes">
          <input id="oidc-scopes" className={inputCls} value={scopes} onChange={(e) => setScopes(e.target.value)} />
        </FormField>
      </details>

      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={busy}>
          Speichern
        </Button>
      </div>
    </form>
  );
}
